import {
  Box, Container, Typography, useMediaQuery,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import CloseIcon from '@mui/icons-material/Close'
import { selectNotification } from '../../../redux/features/notifications/notificationCenter/notificationCenterSlice'
import {
  getNotificationsListWithQueries,
  notificationCenterResetAlertHandler,
} from '../../../redux/features/notifications/notificationCenter/notificationCenterThunk'
import LoaderWithErrorHandler from '../../../components/PageHelpers/LoaderWithErrorHandler'
import NoDataFound from '../../../components/PageHelpers/NoDataFound'
import UseTranslation from '../../../hooks/UseTranslation'
import SEO from '../../../components/PageHelpers/SEO'
import loginBackSvg from '../../../images/loginBackSvg.svg'
import NotificationCenterCards from './NotificationCenterCards'
import NotificationCenterSkeleton from './NotificationCenterSkeleton'
import PageFilter from './PageFilter'
import IconBtn from '../../../components/Buttons/IconBtnV2'
import LoadMoreBtn from '../../../components/PageHelpers/LoadMoreBtn'
import SearchBarToggle from '../../../components/PageHelpers/SearchBarToggle'
import FilterSortIcon from '../../../images/FilterSort.svg'
import TitleWithCountV2 from '../../../components/PageHelpers/TitleWithCountV2'
import AppRoutes from '../../../services/AppRoutes'

const Index = () => {
  const notificationStore = useSelector(selectNotification)
  const dispatch = useDispatch()
  const { t } = UseTranslation()
  const [searchBarExpanded, setSearchBarExpanded] = useState(false)
  const [openFilter, setOpenFilter] = useState(false)
  const isXsScreen = useMediaQuery((theme) => theme.breakpoints.only('xs'))

  useEffect(() => {
    dispatch(getNotificationsListWithQueries({ resetQueries: true }))
  }, [])

  const isFilteredArraysNotEmpty = Object.values(notificationStore?.activeFilters).some(
    (singleFilter) => singleFilter?.length,
  )

  return (
    <Box sx={styles.root}>
      <SEO title={t?.['Notification Center']} />
      <Container maxWidth={false} sx={styles.container}>
        <Box sx={styles.wrapperContainer}>
          <Link to={`${AppRoutes.home}`} style={styles.link}>
            <Box sx={styles.backLink}>
              <ArrowBackIosNewIcon />
              <Typography variant="p">{t?.['Back to Homepage']}</Typography>
            </Box>
          </Link>
          <Box sx={styles.headerContainer}>
            {!searchBarExpanded && (
              <>
            <Link to={`${AppRoutes.home}`} style={styles.link}>
              <Box sx={styles.xsBackLink}>
                <ArrowBackIosNewIcon />
              </Box>
            </Link>
            <TitleWithCountV2
              store={notificationStore}
              title={t?.['Notification Center']}
              icon={<NotificationsNoneOutlinedIcon />}
              iconSize="xl"
              iconColor="warning.dark"
              withCount
              textVariant="heading2"
              customStyle={styles.headerTitle}
                />
                </>
            )}
            <Box sx={styles.filterWithSearch}>
              {(isXsScreen && !searchBarExpanded) && (
              <IconBtn
                icon={openFilter ? <CloseIcon /> : <img src={FilterSortIcon} alt="filter icon" />}
                onClick={() => setOpenFilter(!openFilter)}
                customStyle={styles.xsFilterButton(isFilteredArraysNotEmpty)}
              />
              )}
              <SearchBarToggle
                searchPlaceholder={t?.['Search by Details']}
                actionToDispatch={getNotificationsListWithQueries}
                expandedState={searchBarExpanded}
                setExpandedState={setSearchBarExpanded}
                sendQueryAsObject
                actionProps={{ pageNumber: 0 }}
              />
            </Box>
          </Box>
        </Box>

        <PageFilter
          openModal={openFilter}
          setOpenModal={setOpenFilter}
        />

        <Box sx={styles.sectionContainer}>
          <Box sx={styles.cardHeaderContainer}>
            <Typography variant="heading6">
              {`${t?.Header}`}
            </Typography>
            <Typography variant="heading6">
              {`${t?.Details}`}
            </Typography>
            <Typography variant="heading6" sx={styles.cardDate}>
              {`${t?.['Date - Time']}`}
            </Typography>
          </Box>
          <NotificationCenterCards />
          <LoaderWithErrorHandler
            store={notificationStore}
            resetAlertHandler={notificationCenterResetAlertHandler}
            Skeleton={NotificationCenterSkeleton}
          />
          <LoadMoreBtn
            store={notificationStore}
            actionToDispatch={getNotificationsListWithQueries}
            sendQueryAsObject
          />
          <Box component="img" src={loginBackSvg} alt="login img" sx={styles.backSvg} />
          <NoDataFound store={notificationStore} />
        </Box>
      </Container>
    </Box>
  )
}
const styles = {
  root: {
    display: 'flex',
    marginTop: { xs: '-10px', sm: '-29px' },
    paddingInline: { xs: '10px !important', sm: '18px !important', md: '24px !important' },
  },
  container: {
    padding: { xs: '0px', sm: '0px 25px 0px 25px', md: '0px 15px !important' },
  },
  wrapperContainer: {
    borderBottomLeftRadius: '15px',
    borderBottomRightRadius: '15px',
    backgroundColor: 'background.paper',
  },
  sectionContainer: {
    margin: {
      xs: '10px 0px 0px 0px', sm: '0px 0px 20px 0px', md: '0px 0px 30px 0px',
    },
  },
  cardHeaderContainer: {
    display: { xs: 'none', sm: 'grid' },
    gridTemplateColumns: { xs: '1fr', sm: '1.5fr 4fr 1fr' },
    p: { xs: '0px 40px 0px 40px', sm: '0px 40px 0 90px' },
    margin: '15px 0px 13px',
    gap: { xs: '40px', sm: '10px' },
    '& > span': {
      color: '#2A2A38',
      fontfamily: 'Avenir',
      fontsize: '14px !important',
      fontweight: '500',
    },
  },
  backLink: {
    display: { xs: 'none', sm: 'flex' },
    alignItems: 'center',
    textTransform: 'capitalize',
    color: 'text.disabled',
    textDecoration: 'none ',
    margin: '0',
    padding: '0',
    pt: '5px',
    fontSize: '12px',
    marginBottom: '5px',
    cursor: 'pointer',
    '& span': {
      margin: '0',
    },
    '& svg': {
      height: '11px',
    },
  },
  xsBackLink: {
    display: { xs: 'flex', sm: 'none' },
    alignItems: 'center',
    textTransform: 'capitalize',
    color: 'text.disabled',
    textDecoration: 'none ',
    margin: '0',
    padding: '0',
    fontSize: '12px',
    marginTop: '11px',
    cursor: 'pointer',
    '& svg': {
      height: '11px',
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'flex-start !important',
    p: { xs: 1, sm: '10px 30px 15px' },
    height: { xs: '40px', sm: 'auto' },

  },
  headerTitle: {
    marginBottom: '0px !important',
    display: 'flex',
    alignItems: { xs: 'center', md: 'flex-end' },
    gap: '5px',
  },
  link: {
    textDecoration: 'none',
  },
  backSvg: {
    display: { xs: 'flex', sm: 'none' },
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: -1,
  },

  xsFilterButton: (isFiltered) => ({
    height: '27px',
    width: '27px',
    borderRadius: '10px',
    border: isFiltered ? '1px solid #29CC8B' : '1px solid #e2e8ee',
    backgroundColor: 'white',
    color: 'text.hint',
    overflow: 'hidden',
    '& img': {
      filter: isFiltered && 'brightness(0) saturate(100%) invert(60%) sepia(98%) saturate(331%) hue-rotate(90deg) brightness(91%) contrast(91%)',
      width: '20px',
      height: '20px',
    },
  }),
  filterWithSearch: {
    display: { xs: 'flex', sm: 'none' },
    gap: '5px',
    width: '100%',
    justifyContent: 'flex-end',
    marginTop: '5px',
    '& .searchIcon': {
      width: '27px',
      height: '27px',
    },
    '& img': {
      searchBar: {
        '& .search-bar': {
          marginBottom: { sm: '0px  !important' },
        },
      },
    },
  },
}
export default Index
